import React, { useState, useEffect } from 'react';
import { View, Text, StyleSheet, TouchableOpacity, SafeAreaView, ScrollView, Alert, Linking, Platform, Modal } from 'react-native';
import AppTextInput from '../components/AppTextInput';
import { AuthManager, BusinessOwner, BusinessHours } from '../utils/authManager';
import {
  DEFAULT_CUSTOMER_QUEUE_VISIBILITY,
  normalizeCustomerQueueVisibility,
  type CustomerQueueVisibility,
} from '../types/customerQueueVisibility';
import { BUSINESS_HOURS_AM_OPTIONS, BUSINESS_HOURS_PM_OPTIONS, formatTime24To12 } from '../utils/businessHoursUtils';
import SignOutConfirmModal from '../components/SignOutConfirmModal';
import DeleteAccountConfirmModal from '../components/DeleteAccountConfirmModal';
import { UnifiedAuthService } from '../services/unifiedAuthService';
import paymentProviderService from '../services/paymentProviderService';
import { checkStripeConnectReady } from '../services/deliveryCheckout';
import ManageLocationsModal from '../components/ManageLocationsModal';
import CalendarIntegrationModal from '../components/CalendarIntegrationModal';
import { activeCalendarIntegrationService } from '../services/activeCalendarIntegrationService';
import BusinessCategoryPicker from '../components/BusinessCategoryPicker';
import { getCategoryName, normalizeBusinessCategoryFields } from '../constants/businessCategories';

// TS safety for web-only globals used behind Platform checks.
declare const window: any;

interface SettingsScreenProps {
  onNavigateToServiceManagement: () => void;
  onNavigateToProductManagement: () => void;
  onNavigateToPaymentProviders: () => void;
  onLogout: () => void;
  onBack?: () => void;
}

const SettingsScreen: React.FC<SettingsScreenProps> = ({
  onNavigateToServiceManagement,
  onNavigateToProductManagement,
  onNavigateToPaymentProviders,
  onLogout,
  onBack,
}) => {
  const [businessOwner, setBusinessOwner] = useState<BusinessOwner | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [showSignOutModal, setShowSignOutModal] = useState(false);
  const [showDeleteAccountModal, setShowDeleteAccountModal] = useState(false);
  const [isDeletingAccount, setIsDeletingAccount] = useState(false);
  const [showManageLocations, setShowManageLocations] = useState(false);
  const [showCalendarIntegration, setShowCalendarIntegration] = useState(false);
  const [calendarAutoSyncEnabled, setCalendarAutoSyncEnabled] = useState(false);
  const [timePickerModal, setTimePickerModal] = useState<{
    visible: boolean;
    day: string | null;
    field: 'openTime' | 'closeTime' | null;
  }>({ visible: false, day: null, field: null });
  const daysOfWeek = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
  
  const [formData, setFormData] = useState({
    companyName: '',
    phone: '',
    email: '',
    website: '',
    address: '',
    categoryId: '',
    industry: '',
    companySize: '',
  });
  const [categoryError, setCategoryError] = useState('');
  
  const [businessHours, setBusinessHours] = useState<BusinessHours>({
    Monday: { isOpen: true, openTime: '09:00', closeTime: '17:00' },
    Tuesday: { isOpen: true, openTime: '09:00', closeTime: '17:00' },
    Wednesday: { isOpen: true, openTime: '09:00', closeTime: '17:00' },
    Thursday: { isOpen: true, openTime: '09:00', closeTime: '17:00' },
    Friday: { isOpen: true, openTime: '09:00', closeTime: '17:00' },
    Saturday: { isOpen: false, openTime: '09:00', closeTime: '17:00' },
    Sunday: { isOpen: false, openTime: '09:00', closeTime: '17:00' },
  });
  
  const [appointmentDuration, setAppointmentDuration] = useState<number>(30);
  
  // Business settings
  const [businessSettings, setBusinessSettings] = useState({
    autoAcceptBookings: true,
    paymentRequiredAtBooking: false,
    emailNotifications: true,
  });

  const [customerQueueVisibility, setCustomerQueueVisibility] = useState<CustomerQueueVisibility>(
    DEFAULT_CUSTOMER_QUEUE_VISIBILITY
  );
  const [pickupReadyDisplayEnabled, setPickupReadyDisplayEnabled] = useState(false);
  const [pickupReadySmsEnabled, setPickupReadySmsEnabled] = useState(false);
  const [deliveryEnabled, setDeliveryEnabled] = useState(false);
  const [deliveryPickupAddress, setDeliveryPickupAddress] = useState('');
  const [deliveryPickupPhone, setDeliveryPickupPhone] = useState('');
  const [doordashStoreId, setDoordashStoreId] = useState('');
  const [stripeConnectReady, setStripeConnectReady] = useState(false);

  useEffect(() => {
    loadBusinessOwnerData();
  }, []);

  useEffect(() => {
    const ownerId = businessOwner?.id;
    if (!ownerId) {
      setStripeConnectReady(false);
      return;
    }
    checkStripeConnectReady(ownerId, businessOwner?.stripeConnectAccountId).then(({ ready }) => {
      setStripeConnectReady(ready);
    });
  }, [businessOwner?.id, businessOwner?.stripeConnectAccountId]);

  const loadBusinessOwnerData = async () => {
    try {
      const owner = await AuthManager.getSavedBusinessOwner();
      if (owner) {
        setBusinessOwner(owner);
        setCustomerQueueVisibility(normalizeCustomerQueueVisibility((owner as any).customerQueueVisibility));
        setPickupReadyDisplayEnabled(!!(owner as any).pickupReadyDisplayEnabled);
        setPickupReadySmsEnabled(!!(owner as any).pickupReadySmsEnabled);
        setDeliveryEnabled(!!(owner as any).deliveryEnabled);
        setDeliveryPickupAddress((owner as any).deliveryPickupAddress || '');
        setDeliveryPickupPhone((owner as any).deliveryPickupPhone || owner.phone || '');
        setDoordashStoreId((owner as any).doordashStoreId || '');
        setBusinessSettings(prev => ({
          ...prev,
          paymentRequiredAtBooking: !!(owner as any).paymentRequiredAtBooking,
        }));
        setCalendarAutoSyncEnabled(!!(owner as any).calendarAutoSyncEnabled);
        
        // Handle address - can be string or object
        let addressString = '';
        if (owner.address) {
          if (typeof owner.address === 'string') {
            addressString = owner.address;
          } else if (typeof owner.address === 'object') {
            const addr = owner.address as { street?: string; city?: string; state?: string; zipcode?: string; country?: string };
            // If street holds the full address (e.g. from Settings edit), use it alone to avoid duplicating zipcode
            if (addr.street && (addr.street.includes(',') || addr.street.length > 25)) {
              addressString = addr.street;
            } else {
              const parts = [addr.street, addr.city, addr.state, addr.zipcode, addr.country].filter(Boolean);
              addressString = parts.join(', ');
            }
          }
        }
        if (!deliveryPickupAddress && addressString) {
          setDeliveryPickupAddress(addressString);
        }
        
        const categoryNormalized = normalizeBusinessCategoryFields(
          (owner as any).categoryId,
          owner.industry,
        );

        setFormData({
          companyName: owner.companyName || owner.name || '',
          phone: owner.phone || '',
          email: owner.email || '',
          website: owner.website || '',
          address: addressString,
          categoryId: categoryNormalized.categoryId,
          industry: categoryNormalized.industry,
          companySize: owner.companySize || '',
        });
        
        // Load business hours and appointment duration
        if (owner.businessHours) {
          setBusinessHours(owner.businessHours);
        }
        if (owner.appointmentDuration) {
          setAppointmentDuration(owner.appointmentDuration);
        }
      }
    } catch (error) {
      console.error('Error loading business owner data:', error);
    }
  };

  const handleSave = async () => {
    try {
      if (!businessOwner) return;

      if (!formData.categoryId) {
        setCategoryError('Please search and select your industry.');
        Alert.alert('Industry required', 'Select your business industry so booking features work correctly.');
        return;
      }
      setCategoryError('');

      const updatedOwner = {
        ...businessOwner,
        companyName: formData.companyName,
        name: formData.companyName,
        phone: formData.phone,
        email: formData.email,
        website: formData.website,
        address: formData.address,
        categoryId: formData.categoryId,
        industry: formData.industry || getCategoryName(formData.categoryId),
        companySize: formData.companySize,
        businessHours: businessHours,
        appointmentDuration: appointmentDuration,
        paymentRequiredAtBooking: businessSettings.paymentRequiredAtBooking,
        customerQueueVisibility,
        pickupReadyDisplayEnabled,
        pickupReadySmsEnabled,
        calendarAutoSyncEnabled,
      };

      await AuthManager.saveBusinessOwner(updatedOwner);
      const categoryNormalized = normalizeBusinessCategoryFields(
        updatedOwner.categoryId,
        updatedOwner.industry,
      );
      const savedOwner = {
        ...updatedOwner,
        categoryId: categoryNormalized.categoryId,
        industry: categoryNormalized.industry,
      };
      setBusinessOwner(savedOwner);
      setFormData((prev) => ({
        ...prev,
        website: savedOwner.website || '',
        categoryId: categoryNormalized.categoryId,
        industry: categoryNormalized.industry,
      }));
      setIsEditing(false);
      Alert.alert('Success', 'Company information updated successfully!');
    } catch (error) {
      console.error('Error saving business owner data:', error);
      Alert.alert('Error', 'Failed to save changes. Please try again.');
    }
  };

  const persistCalendarAutoSync = async (next: boolean) => {
    if (!businessOwner?.id) return;
    const previous = calendarAutoSyncEnabled;
    setCalendarAutoSyncEnabled(next);
    try {
      const updatedOwner = { ...businessOwner, calendarAutoSyncEnabled: next };
      await AuthManager.saveBusinessOwner(updatedOwner);
      setBusinessOwner(updatedOwner);
      const { refreshBusinessCalendarAutoSync } = await import('../services/businessCalendarAutoSyncService');
      await refreshBusinessCalendarAutoSync();
    } catch (error) {
      console.error('Error saving calendar auto-sync:', error);
      setCalendarAutoSyncEnabled(previous);
      Alert.alert('Error', 'Could not save automatic calendar sync. Please try again.');
    }
  };

  const handleCalendarAutoSyncToggle = async () => {
    if (calendarAutoSyncEnabled) {
      await persistCalendarAutoSync(false);
      return;
    }
    try {
      const accounts = await activeCalendarIntegrationService.getConnectedAccounts();
      const hasConnected = accounts.some(a => a.isConnected);
      if (!hasConnected) {
        setShowCalendarIntegration(true);
        return;
      }
    } catch (error) {
      console.error('Error checking connected calendars:', error);
      Alert.alert(
        'Could not verify calendars',
        'We could not check your calendar connections. Open Calendar integration to connect a calendar, then try again.',
        [
          { text: 'Cancel', style: 'cancel' },
          { text: 'Open Calendar integration', onPress: () => setShowCalendarIntegration(true) },
        ],
      );
      return;
    }
    await persistCalendarAutoSync(true);
  };

  const persistCustomerQueueVisibility = async (tier: CustomerQueueVisibility) => {
    if (!businessOwner?.id) return;
    const previous = customerQueueVisibility;
    setCustomerQueueVisibility(tier);
    try {
      const updatedOwner = { ...businessOwner, customerQueueVisibility: tier };
      await AuthManager.saveBusinessOwner(updatedOwner);
      setBusinessOwner(updatedOwner);
    } catch (error) {
      console.error('Error saving customer queue visibility:', error);
      setCustomerQueueVisibility(previous);
      Alert.alert('Error', 'Could not save queue visibility. Please try again.');
    }
  };

  const persistPickupReadyDisplay = async (next: boolean) => {
    if (!businessOwner?.id) return;
    const prevDisplay = pickupReadyDisplayEnabled;
    const prevSms = pickupReadySmsEnabled;
    setPickupReadyDisplayEnabled(next);
    if (!next) setPickupReadySmsEnabled(false);
    try {
      const updatedOwner = {
        ...businessOwner,
        pickupReadyDisplayEnabled: next,
        pickupReadySmsEnabled: next ? pickupReadySmsEnabled : false,
      };
      await AuthManager.saveBusinessOwner(updatedOwner);
      setBusinessOwner(updatedOwner);
    } catch (error) {
      console.error('Error saving pickup display setting:', error);
      setPickupReadyDisplayEnabled(prevDisplay);
      setPickupReadySmsEnabled(prevSms);
      Alert.alert('Error', 'Could not save pickup settings. Please try again.');
    }
  };

  const persistPickupReadySms = async (next: boolean) => {
    if (!businessOwner?.id || !pickupReadyDisplayEnabled) return;
    const previous = pickupReadySmsEnabled;
    setPickupReadySmsEnabled(next);
    try {
      const updatedOwner = { ...businessOwner, pickupReadyDisplayEnabled, pickupReadySmsEnabled: next };
      await AuthManager.saveBusinessOwner(updatedOwner);
      setBusinessOwner(updatedOwner);
    } catch (error) {
      console.error('Error saving pickup SMS setting:', error);
      setPickupReadySmsEnabled(previous);
      Alert.alert('Error', 'Could not save SMS setting. Please try again.');
    }
  };

  const togglePaymentRequiredAtBooking = async () => {
    const nextValue = !businessSettings.paymentRequiredAtBooking;
    let ownerId = businessOwner?.id;
    if (!ownerId && Platform.OS === 'web') {
      try {
        const { getWebAuth } = await import('../services/unifiedAuthService');
        ownerId = getWebAuth?.()?.currentUser?.uid || '';
      } catch {
        ownerId = '';
      }
    }
    if (!ownerId) return;

    // Always allow turning OFF without checks.
    if (!nextValue) {
      setBusinessSettings(prev => ({ ...prev, paymentRequiredAtBooking: false }));
      try {
        const updatedOwner = { ...businessOwner!, id: ownerId, paymentRequiredAtBooking: false };
        await AuthManager.saveBusinessOwner(updatedOwner);
        setBusinessOwner(updatedOwner);
      } catch (error) {
        console.error('Error saving paymentRequiredAtBooking:', error);
        // revert UI if save fails
        setBusinessSettings(prev => ({ ...prev, paymentRequiredAtBooking: true }));
        Alert.alert('Error', 'Could not save payment setting. Please try again.');
      }
      return;
    }

    // Turning ON: require PayPal.me linked so customers can pay at booking.
    try {
      const paypalStatus = await paymentProviderService.checkPayPalStatus(ownerId);
      if (!paypalStatus.connected || !paypalStatus.ready) {
        Alert.alert(
          'PayPal required',
          'To require payment at booking, link your PayPal.me in Payment Providers first.',
          [
            { text: 'Cancel', style: 'cancel' },
            {
              text: 'Link PayPal',
              onPress: onNavigateToPaymentProviders,
            },
          ]
        );
        setBusinessSettings(prev => ({ ...prev, paymentRequiredAtBooking: false }));
        return;
      }

      setBusinessSettings(prev => ({ ...prev, paymentRequiredAtBooking: true }));
      const updatedOwner = { ...businessOwner!, id: ownerId, paymentRequiredAtBooking: true };
      await AuthManager.saveBusinessOwner(updatedOwner);
      setBusinessOwner(updatedOwner);
    } catch (error) {
      console.error('Error validating PayPal status:', error);
      Alert.alert(
        'Could not verify PayPal',
        'We could not confirm your PayPal link right now. Try again, or link PayPal in Payment Providers.',
        [
          { text: 'OK', style: 'cancel' },
          { text: 'Payment Providers', onPress: onNavigateToPaymentProviders },
        ]
      );
      setBusinessSettings(prev => ({ ...prev, paymentRequiredAtBooking: false }));
    }
  };

  const persistDeliveryFields = async (fields: {
    deliveryEnabled?: boolean;
    deliveryPickupAddress?: string;
    deliveryPickupPhone?: string;
    doordashStoreId?: string;
  }) => {
    if (!businessOwner?.id) return;
    const updatedOwner = {
      ...businessOwner,
      ...fields,
    };
    await AuthManager.saveBusinessOwner(updatedOwner);
    setBusinessOwner(updatedOwner);
  };

  const toggleDeliveryEnabled = async () => {
    const nextValue = !deliveryEnabled;
    const ownerId = businessOwner?.id;
    if (!ownerId) return;

    if (!nextValue) {
      setDeliveryEnabled(false);
      try {
        await persistDeliveryFields({ deliveryEnabled: false });
      } catch {
        setDeliveryEnabled(true);
        Alert.alert('Error', 'Could not save delivery setting.');
      }
      return;
    }

    try {
      const { ready } = await checkStripeConnectReady(
        ownerId,
        businessOwner?.stripeConnectAccountId
      );
      if (!ready) {
        Alert.alert(
          'Stripe Connect required',
          'Open Payment Providers and tap Link Stripe on the Stripe Connect card. Delivery checkout needs Stripe; pickup can still use PayPal.',
          [
            { text: 'Cancel', style: 'cancel' },
            { text: 'Open Payment Providers', onPress: onNavigateToPaymentProviders },
          ]
        );
        return;
      }
      const pickupAddr = deliveryPickupAddress.trim() || formData.address.trim();
      if (!pickupAddr) {
        Alert.alert(
          'Pickup address required',
          'Set your business address (or delivery pickup address) so couriers know where to pick up orders.'
        );
        return;
      }
      if (!deliveryPickupPhone.trim()) {
        Alert.alert('Phone required', 'Enter the phone number couriers should call at pickup.');
        return;
      }
      setDeliveryEnabled(true);
      await persistDeliveryFields({
        deliveryEnabled: true,
        deliveryPickupAddress: pickupAddr,
        deliveryPickupPhone: deliveryPickupPhone.trim(),
        doordashStoreId: doordashStoreId.trim(),
      });
      setDeliveryPickupAddress(pickupAddr);
    } catch (error) {
      console.error('toggleDeliveryEnabled:', error);
      setDeliveryEnabled(false);
      Alert.alert('Error', 'Could not enable delivery. Try again.');
    }
  };

  const handleCancel = () => {
    if (businessOwner) {
      let addressString = '';
      if (businessOwner.address) {
        if (typeof businessOwner.address === 'string') {
          addressString = businessOwner.address;
        } else {
          const addr = businessOwner.address as { street?: string; city?: string; zipcode?: string };
          addressString = addr.street && (addr.street.includes(',') || addr.street.length > 25)
            ? addr.street
            : [addr.street, (businessOwner.address as any).city, (businessOwner.address as any).state, addr.zipcode, (businessOwner.address as any).country].filter(Boolean).join(', ');
        }
      }
      setFormData({
        companyName: businessOwner.companyName || businessOwner.name || '',
        phone: businessOwner.phone || '',
        email: businessOwner.email || '',
        website: businessOwner.website || '',
        address: addressString,
        categoryId: normalizeBusinessCategoryFields(
          businessOwner.categoryId,
          businessOwner.industry,
        ).categoryId,
        industry: normalizeBusinessCategoryFields(
          businessOwner.categoryId,
          businessOwner.industry,
        ).industry,
        companySize: businessOwner.companySize || '',
      });
    }
    setCategoryError('');
    setIsEditing(false);
  };

  const handleLogout = () => {
    setShowSignOutModal(true);
  };

  const handleDeleteAccountConfirm = async () => {
    if (isDeletingAccount) return;
    setIsDeletingAccount(true);
    try {
      await UnifiedAuthService.deleteAccount();
      setShowDeleteAccountModal(false);
      onLogout();
    } catch (error: any) {
      Alert.alert(
        'Could not delete account',
        error?.message || 'Something went wrong. Please try again or contact support.'
      );
    } finally {
      setIsDeletingAccount(false);
    }
  };

  const handleServiceManagement = () => {
    onNavigateToServiceManagement();
  };

  const handleProductManagement = () => {
    onNavigateToProductManagement();
  };

  if (!businessOwner) {
    return (
      <SafeAreaView style={styles.container}>
        <View style={styles.header}>
          <Text style={styles.headerTitle}>Settings</Text>
        </View>
        <View style={styles.loadingContainer}>
          <Text style={styles.loadingText}>Loading company information...</Text>
        </View>
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView style={styles.container}>
      <SignOutConfirmModal
        visible={showSignOutModal}
        onCancel={() => setShowSignOutModal(false)}
        onConfirm={() => {
          setShowSignOutModal(false);
          onLogout();
        }}
      />
      <View style={styles.header}>
        <View style={styles.headerLeft}>
          {onBack && (
            <TouchableOpacity style={styles.backButton} onPress={onBack}>
              <Text style={styles.backButtonText}>← Back</Text>
            </TouchableOpacity>
          )}
        </View>
        <Text style={styles.headerTitle} numberOfLines={1}>Company Settings</Text>
        <TouchableOpacity
          style={styles.editButton}
          onPress={() => {
            if (isEditing) {
              handleCancel();
            } else {
              setIsEditing(true);
            }
          }}
        >
          <Text style={styles.editButtonText}>
            {isEditing ? 'Cancel' : 'Edit'}
          </Text>
        </TouchableOpacity>
      </View>

      <ScrollView 
        style={styles.content}
        scrollEnabled={true}
        showsVerticalScrollIndicator={true}
      >
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Company Information</Text>
          
          {/* Company Name */}
          <View style={styles.inputGroup}>
            <Text style={styles.label}>Company Name *</Text>
            {isEditing ? (
              <AppTextInput
                style={styles.input}
                value={formData.companyName}
                onChangeText={(text) => setFormData({...formData, companyName: text})}
                placeholder="Enter company name"
                autoCapitalize="words"
              />
            ) : (
              <Text style={styles.valueText}>{formData.companyName || 'Not set'}</Text>
            )}
          </View>

          {/* Phone */}
          <View style={styles.inputGroup}>
            <Text style={styles.label}>Phone Number *</Text>
            {isEditing ? (
              <AppTextInput
                style={styles.input}
                value={formData.phone}
                onChangeText={(text) => setFormData({...formData, phone: text})}
                placeholder="Enter phone number"
                keyboardType="phone-pad"
              />
            ) : (
              <Text style={styles.valueText}>{formData.phone || 'Not set'}</Text>
            )}
          </View>

          {/* Email */}
          <View style={styles.inputGroup}>
            <Text style={styles.label}>Email Address *</Text>
            {isEditing ? (
              <AppTextInput
                style={styles.input}
                value={formData.email}
                onChangeText={(text) => setFormData({...formData, email: text})}
                placeholder="Enter email address"
                keyboardType="email-address"
                autoCapitalize="none"
              />
            ) : (
              <Text style={styles.valueText}>{formData.email || 'Not set'}</Text>
            )}
          </View>

          {/* Website */}
          <View style={styles.inputGroup}>
            <Text style={styles.label}>Website</Text>
            {isEditing ? (
              <AppTextInput
                style={styles.input}
                value={formData.website}
                onChangeText={(text) => setFormData({...formData, website: text})}
                placeholder="Enter website URL"
                keyboardType="url"
                autoCapitalize="none"
              />
            ) : (
              <Text style={styles.valueText}>{formData.website || 'Not set'}</Text>
            )}
          </View>

          {/* Address */}
          <View style={styles.inputGroup}>
            <Text style={styles.label}>Address *</Text>
            {isEditing ? (
              <AppTextInput
                style={[styles.input, styles.multilineInput]}
                value={formData.address}
                onChangeText={(text) => setFormData({...formData, address: text})}
                placeholder="Enter company address"
                multiline
                numberOfLines={3}
              />
            ) : (
              <Text style={styles.valueText}>
                {formData.address && typeof formData.address === 'string' 
                  ? formData.address 
                  : formData.address && typeof formData.address === 'object'
                  ? `${(formData.address as any).street || ''}, ${(formData.address as any).city || ''}, ${(formData.address as any).state || ''} ${(formData.address as any).zipcode || ''}`.trim().replace(/^,\s*|,\s*$/g, '')
                  : 'Not set'}
              </Text>
            )}
          </View>

          {/* Industry */}
          <View style={styles.inputGroup}>
            <BusinessCategoryPicker
              label="Industry"
              categoryId={formData.categoryId}
              disabled={!isEditing}
              error={categoryError}
              onSelect={(categoryId, categoryName) => {
                setFormData((prev) => ({
                  ...prev,
                  categoryId,
                  industry: categoryName,
                }));
                setCategoryError('');
              }}
            />
          </View>

          {/* Company Size */}
          <View style={styles.inputGroup}>
            <Text style={styles.label}>Company Size</Text>
            {isEditing ? (
              <AppTextInput
                style={styles.input}
                value={formData.companySize}
                onChangeText={(text) => setFormData({...formData, companySize: text})}
                placeholder="Enter company size"
              />
            ) : (
              <Text style={styles.valueText}>{formData.companySize || 'Not set'}</Text>
            )}
          </View>

          {isEditing && (
            <View style={styles.buttonContainer}>
              <TouchableOpacity style={styles.saveButton} onPress={handleSave}>
                <Text style={styles.saveButtonText}>Save Changes</Text>
              </TouchableOpacity>
              <TouchableOpacity style={styles.cancelButton} onPress={handleCancel}>
                <Text style={styles.cancelButtonText}>Cancel</Text>
              </TouchableOpacity>
            </View>
          )}
        </View>

        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Business Management</Text>
          
          <TouchableOpacity style={styles.settingItem} onPress={() => setShowManageLocations(true)}>
            <View style={styles.settingLeft}>
              <Text style={styles.settingIcon}>📍</Text>
              <View style={styles.settingText}>
                <Text style={styles.settingTitle}>Locations</Text>
                <Text style={styles.settingSubtitle}>Add and manage multiple locations</Text>
              </View>
            </View>
            <Text style={styles.settingArrow}>›</Text>
          </TouchableOpacity>

          <TouchableOpacity style={styles.settingItem} onPress={handleServiceManagement}>
            <View style={styles.settingLeft}>
              <Text style={styles.settingIcon}>💇‍♀️</Text>
              <View style={styles.settingText}>
                <Text style={styles.settingTitle}>Service Management</Text>
                <Text style={styles.settingSubtitle}>Manage services and pricing</Text>
              </View>
            </View>
            <Text style={styles.settingArrow}>›</Text>
          </TouchableOpacity>

          <TouchableOpacity style={styles.settingItem} onPress={handleProductManagement}>
            <View style={styles.settingLeft}>
              <Text style={styles.settingIcon}>🛒</Text>
              <View style={styles.settingText}>
                <Text style={styles.settingTitle}>Product Management</Text>
                <Text style={styles.settingSubtitle}>Manage products and inventory</Text>
              </View>
            </View>
            <Text style={styles.settingArrow}>›</Text>
          </TouchableOpacity>

          <TouchableOpacity style={styles.settingItem} onPress={onNavigateToPaymentProviders}>
            <View style={styles.settingLeft}>
              <Text style={styles.settingIcon}>💳</Text>
              <View style={styles.settingText}>
                <Text style={styles.settingTitle}>Payment Providers</Text>
                <Text style={styles.settingSubtitle}>
                  PayPal.me for pickup · Stripe Connect for delivery
                </Text>
              </View>
            </View>
            <Text style={styles.settingArrow}>›</Text>
          </TouchableOpacity>

          <TouchableOpacity style={styles.settingItem} onPress={onNavigateToPaymentProviders}>
            <View style={styles.settingLeft}>
              <Text style={styles.settingIcon}>💜</Text>
              <View style={styles.settingText}>
                <Text style={styles.settingTitle}>Stripe Connect</Text>
                <Text style={styles.settingSubtitle}>
                  {stripeConnectReady
                    ? 'Connected — you can enable delivery'
                    : 'Required for delivery — tap to link Stripe'}
                </Text>
              </View>
            </View>
            <Text style={styles.settingArrow}>›</Text>
          </TouchableOpacity>

          <TouchableOpacity style={styles.settingItem} onPress={toggleDeliveryEnabled}>
            <View style={styles.settingLeft}>
              <Text style={styles.settingIcon}>🚗</Text>
              <View style={styles.settingText}>
                <Text style={styles.settingTitle}>Enable delivery</Text>
                <Text style={styles.settingSubtitle}>
                  DoorDash Drive — requires Stripe Connect; service fee in Delivery &amp; fees
                </Text>
              </View>
            </View>
            <View style={[styles.toggle, deliveryEnabled && styles.toggleActive]}>
              <View style={[styles.toggleThumb, deliveryEnabled && styles.toggleThumbActive]} />
            </View>
          </TouchableOpacity>

          {deliveryEnabled ? (
            <View style={styles.inputGroup}>
              <Text style={styles.label}>Courier pickup address</Text>
              <AppTextInput
                style={styles.input}
                value={deliveryPickupAddress}
                onChangeText={setDeliveryPickupAddress}
                placeholder="Full address for DoorDash pickup"
                onBlur={() => {
                  void persistDeliveryFields({
                    deliveryPickupAddress: deliveryPickupAddress.trim(),
                    deliveryPickupPhone: deliveryPickupPhone.trim(),
                    doordashStoreId: doordashStoreId.trim(),
                  });
                }}
              />
              <Text style={styles.label}>Pickup phone (E.164 or 10-digit)</Text>
              <AppTextInput
                style={styles.input}
                value={deliveryPickupPhone}
                onChangeText={setDeliveryPickupPhone}
                placeholder="(555) 555-0100"
                keyboardType="phone-pad"
              />
              <Text style={styles.label}>DoorDash store ID (optional)</Text>
              <AppTextInput
                style={styles.input}
                value={doordashStoreId}
                onChangeText={setDoordashStoreId}
                placeholder="From your DoorDash merchant portal"
              />
            </View>
          ) : null}

          <TouchableOpacity style={styles.settingItem} onPress={() => setShowCalendarIntegration(true)}>
            <View style={styles.settingLeft}>
              <Text style={styles.settingIcon}>📅</Text>
              <View style={styles.settingText}>
                <Text style={styles.settingTitle}>Calendar integration</Text>
                <Text style={styles.settingSubtitle}>
                  Show bookings on Google, Outlook, or Apple Calendar on this device
                </Text>
              </View>
            </View>
            <Text style={styles.settingArrow}>›</Text>
          </TouchableOpacity>

          <TouchableOpacity
            style={styles.settingItem}
            onPress={handleCalendarAutoSyncToggle}
          >
            <View style={styles.settingLeft}>
              <Text style={styles.settingIcon}>🔄</Text>
              <View style={styles.settingText}>
                <Text style={styles.settingTitle}>Automatic calendar sync</Text>
                <Text style={styles.settingSubtitle}>
                  When on, new bookings sync to connected calendars while BookAPT is open (see Calendar integration)
                </Text>
              </View>
            </View>
            <View style={[styles.toggle, calendarAutoSyncEnabled && styles.toggleActive]}>
              <View style={[styles.toggleThumb, calendarAutoSyncEnabled && styles.toggleThumbActive]} />
            </View>
          </TouchableOpacity>

        </View>

        {/* Modals */}
        {businessOwner?.id && (
          <ManageLocationsModal
            visible={showManageLocations}
            onClose={() => setShowManageLocations(false)}
            businessId={businessOwner.id}
          />
        )}

        <CalendarIntegrationModal
          visible={showCalendarIntegration}
          onClose={() => setShowCalendarIntegration(false)}
          businessOwner={businessOwner}
        />

        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Business Settings</Text>
          
          <TouchableOpacity 
            style={styles.settingItem}
            onPress={() => setBusinessSettings(prev => ({ ...prev, autoAcceptBookings: !prev.autoAcceptBookings }))}
          >
            <View style={styles.settingLeft}>
              <Text style={styles.settingIcon}>✅</Text>
              <View style={styles.settingText}>
                <Text style={styles.settingTitle}>Auto Accept Bookings</Text>
                <Text style={styles.settingSubtitle}>Automatically accept new bookings</Text>
              </View>
            </View>
            <View style={[styles.toggle, businessSettings.autoAcceptBookings && styles.toggleActive]}>
              <View style={[styles.toggleThumb, businessSettings.autoAcceptBookings && styles.toggleThumbActive]} />
            </View>
          </TouchableOpacity>

          <TouchableOpacity 
            style={styles.settingItem}
            onPress={togglePaymentRequiredAtBooking}
          >
            <View style={styles.settingLeft}>
              <Text style={styles.settingIcon}>💰</Text>
              <View style={styles.settingText}>
                <Text style={styles.settingTitle}>Payment required at booking</Text>
                <Text style={styles.settingSubtitle}>
                  Require Pay Now (PayPal) — hide Confirm booking when prices are set
                </Text>
              </View>
            </View>
            <View style={[styles.toggle, businessSettings.paymentRequiredAtBooking && styles.toggleActive]}>
              <View style={[styles.toggleThumb, businessSettings.paymentRequiredAtBooking && styles.toggleThumbActive]} />
            </View>
          </TouchableOpacity>

          <TouchableOpacity 
            style={styles.settingItem}
            onPress={() => setBusinessSettings(prev => ({ ...prev, emailNotifications: !prev.emailNotifications }))}
          >
            <View style={styles.settingLeft}>
              <Text style={styles.settingIcon}>📧</Text>
              <View style={styles.settingText}>
                <Text style={styles.settingTitle}>Email Notifications</Text>
                <Text style={styles.settingSubtitle}>Receive email notifications</Text>
              </View>
            </View>
            <View style={[styles.toggle, businessSettings.emailNotifications && styles.toggleActive]}>
              <View style={[styles.toggleThumb, businessSettings.emailNotifications && styles.toggleThumbActive]} />
            </View>
          </TouchableOpacity>

          <View style={styles.queueVisibilitySection}>
            <Text style={styles.queueVisibilitySectionTitle}>Customer queue visibility</Text>
            <Text style={styles.queueVisibilitySectionHint}>
              Controls what customers see on My Appointments for today’s visit (position, ETA, or status only).
            </Text>
            {(
              [
                {
                  id: 'off' as const,
                  title: 'Off',
                  subtitle: 'Customers do not see queue information in the app.',
                },
                {
                  id: 'status_only' as const,
                  title: 'Status only',
                  subtitle: 'Check-in and in-service status only. No queue numbers or ETA.',
                },
                {
                  id: 'full' as const,
                  title: 'Full live queue',
                  subtitle: 'Queue position, ahead count, and ETA when available.',
                },
              ] as const
            ).map((opt) => (
              <TouchableOpacity
                key={opt.id}
                style={[
                  styles.queueVisibilityOption,
                  customerQueueVisibility === opt.id && styles.queueVisibilityOptionSelected,
                ]}
                onPress={() => persistCustomerQueueVisibility(opt.id)}
              >
                <Text style={styles.queueVisibilityOptionTitle}>{opt.title}</Text>
                <Text style={styles.queueVisibilityOptionSubtitle}>{opt.subtitle}</Text>
              </TouchableOpacity>
            ))}
          </View>

          <View style={styles.queueVisibilitySection}>
            <Text style={styles.queueVisibilitySectionTitle}>Pickup-style visits (optional)</Text>
            <Text style={styles.queueVisibilitySectionHint}>
              For shops where customers collect a vehicle or order when work is done. When on, staff “Start” shows as
              In progress and “Complete” shows as Ready for pickup on the customer’s appointments.
            </Text>
            <TouchableOpacity
              style={styles.settingItem}
              onPress={() => persistPickupReadyDisplay(!pickupReadyDisplayEnabled)}
            >
              <View style={styles.settingLeft}>
                <Text style={styles.settingIcon}>🚗</Text>
                <View style={styles.settingText}>
                  <Text style={styles.settingTitle}>Ready for pickup wording</Text>
                  <Text style={styles.settingSubtitle}>Customer app shows In progress / Ready for pickup</Text>
                </View>
              </View>
              <View style={[styles.toggle, pickupReadyDisplayEnabled && styles.toggleActive]}>
                <View style={[styles.toggleThumb, pickupReadyDisplayEnabled && styles.toggleThumbActive]} />
              </View>
            </TouchableOpacity>
            <TouchableOpacity
              style={[styles.settingItem, !pickupReadyDisplayEnabled && { opacity: 0.45 }]}
              onPress={() => pickupReadyDisplayEnabled && persistPickupReadySms(!pickupReadySmsEnabled)}
              disabled={!pickupReadyDisplayEnabled}
            >
              <View style={styles.settingLeft}>
                <Text style={styles.settingIcon}>📱</Text>
                <View style={styles.settingText}>
                  <Text style={styles.settingTitle}>SMS when marked complete</Text>
                  <Text style={styles.settingSubtitle}>
                    Sends one SMS when staff marks the visit complete (requires wording option above)
                  </Text>
                </View>
              </View>
              <View style={[styles.toggle, pickupReadySmsEnabled && styles.toggleActive]}>
                <View style={[styles.toggleThumb, pickupReadySmsEnabled && styles.toggleThumbActive]} />
              </View>
            </TouchableOpacity>
          </View>

        </View>

        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Business Hours & Appointments</Text>
          
          {/* Appointment Duration */}
          <View style={styles.inputGroup}>
            <Text style={styles.label}>Duration Between Appointments *</Text>
            {isEditing ? (
              <View style={styles.durationContainer}>
                <AppTextInput
                  style={styles.durationInput}
                  value={appointmentDuration?.toString() || ''}
                  onChangeText={(text) => {
                    const numValue = parseInt(text) || 30;
                    setAppointmentDuration(numValue);
                  }}
                  placeholder="30"
                  keyboardType="numeric"
                  maxLength={3}
                />
                <Text style={styles.durationUnit}>minutes</Text>
              </View>
            ) : (
              <Text style={styles.valueText}>{appointmentDuration || 30} minutes</Text>
            )}
          </View>

          {/* Business Hours */}
          <View style={styles.inputGroup}>
            <Text style={styles.label}>Business Hours *</Text>
            <View style={styles.hoursContainer}>
              {daysOfWeek.map((day) => (
                <View key={day} style={styles.hoursRowCompact}>
                  <View style={styles.hoursDayRow}>
                    <Text style={styles.hoursDayLabelCompact}>{day.substring(0, 3)}</Text>
                    {isEditing && (
                      <TouchableOpacity
                        style={[
                          styles.toggleSwitchCompact,
                          businessHours[day]?.isOpen && styles.toggleSwitchActive
                        ]}
                        onPress={() => {
                          setBusinessHours(prev => ({
                            ...prev,
                            [day]: {
                              ...prev[day],
                              isOpen: !prev[day]?.isOpen
                            }
                          }));
                        }}
                      >
                        <Text style={styles.toggleSwitchText}>
                          {businessHours[day]?.isOpen ? 'Open' : 'Closed'}
                        </Text>
                      </TouchableOpacity>
                    )}
                  </View>
                  {businessHours[day]?.isOpen && (
                    <View style={styles.hoursTimeContainerCompact}>
                      {isEditing ? (
                        <>
                          <TouchableOpacity
                            style={styles.timeInputCompact}
                            onPress={() => setTimePickerModal({ visible: true, day, field: 'openTime' })}
                          >
                            <Text style={styles.timeInputCompactText}>
                              {formatTime24To12(businessHours[day]?.openTime || '09:00')}
                            </Text>
                            <Text style={styles.timeInputCompactChevron}>▼</Text>
                          </TouchableOpacity>
                          <Text style={styles.hoursSeparatorCompact}>-</Text>
                          <TouchableOpacity
                            style={styles.timeInputCompact}
                            onPress={() => setTimePickerModal({ visible: true, day, field: 'closeTime' })}
                          >
                            <Text style={styles.timeInputCompactText}>
                              {formatTime24To12(businessHours[day]?.closeTime || '17:00')}
                            </Text>
                            <Text style={styles.timeInputCompactChevron}>▼</Text>
                          </TouchableOpacity>
                        </>
                      ) : (
                        <Text style={styles.valueTextCompact}>
                          {formatTime24To12(businessHours[day]?.openTime || '09:00')} - {formatTime24To12(businessHours[day]?.closeTime || '17:00')}
                        </Text>
                      )}
                    </View>
                  )}
                  {!businessHours[day]?.isOpen && !isEditing && (
                    <Text style={styles.valueTextCompact}>Closed</Text>
                  )}
                </View>
              ))}
            </View>
          </View>
          
          {isEditing && (
            <View style={styles.buttonContainer}>
              <TouchableOpacity style={styles.saveButton} onPress={handleSave}>
                <Text style={styles.saveButtonText}>Save Hours & Duration</Text>
              </TouchableOpacity>
            </View>
          )}
        </View>

        {/* Business Hours Time Picker Modal */}
        <Modal
          visible={timePickerModal.visible}
          transparent={true}
          animationType="slide"
          onRequestClose={() => setTimePickerModal({ visible: false, day: null, field: null })}
        >
          <TouchableOpacity
            style={styles.timePickerOverlay}
            activeOpacity={1}
            onPress={() => setTimePickerModal({ visible: false, day: null, field: null })}
          >
            <View style={styles.timePickerModal} onStartShouldSetResponder={() => true}>
              <View style={styles.timePickerHeader}>
                <Text style={styles.timePickerTitle}>
                  {timePickerModal.field === 'openTime' ? 'Open' : 'Close'} time
                  {timePickerModal.day ? ` (${timePickerModal.day})` : ''}
                </Text>
                <TouchableOpacity
                  onPress={() => setTimePickerModal({ visible: false, day: null, field: null })}
                >
                  <Text style={styles.timePickerClose}>✕</Text>
                </TouchableOpacity>
              </View>
              <ScrollView style={styles.timePickerList}>
                <Text style={styles.timePickerSectionHeader}>AM</Text>
                {BUSINESS_HOURS_AM_OPTIONS.map((opt) => {
                  const day = timePickerModal.day;
                  const field = timePickerModal.field;
                  const current = day && field ? businessHours[day]?.[field] || '' : '';
                  const selected = current === opt.value;
                  return (
                    <TouchableOpacity
                      key={opt.value}
                      style={styles.timePickerOption}
                      onPress={() => {
                        if (day && field) {
                          setBusinessHours((prev) => ({
                            ...prev,
                            [day]: {
                              ...prev[day],
                              [field]: opt.value,
                            },
                          }));
                        }
                        setTimePickerModal({ visible: false, day: null, field: null });
                      }}
                    >
                      <Text style={styles.timePickerOptionText}>{opt.label}</Text>
                      {selected && <Text style={styles.timePickerCheckmark}>✓</Text>}
                    </TouchableOpacity>
                  );
                })}
                <Text style={styles.timePickerSectionHeader}>PM</Text>
                {BUSINESS_HOURS_PM_OPTIONS.map((opt) => {
                  const day = timePickerModal.day;
                  const field = timePickerModal.field;
                  const current = day && field ? businessHours[day]?.[field] || '' : '';
                  const selected = current === opt.value;
                  return (
                    <TouchableOpacity
                      key={opt.value}
                      style={styles.timePickerOption}
                      onPress={() => {
                        if (day && field) {
                          setBusinessHours((prev) => ({
                            ...prev,
                            [day]: {
                              ...prev[day],
                              [field]: opt.value,
                            },
                          }));
                        }
                        setTimePickerModal({ visible: false, day: null, field: null });
                      }}
                    >
                      <Text style={styles.timePickerOptionText}>{opt.label}</Text>
                      {selected && <Text style={styles.timePickerCheckmark}>✓</Text>}
                    </TouchableOpacity>
                  );
                })}
              </ScrollView>
            </View>
          </TouchableOpacity>
        </Modal>

        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Legal & Support</Text>
          
          <TouchableOpacity 
            style={styles.settingItem}
            onPress={async () => {
              const url = 'https://bookapt.io/privacy.html';
              try {
                if (Platform.OS === 'web' && typeof window !== 'undefined') {
                  window.open(url, '_blank', 'noopener,noreferrer');
                } else {
                  const supported = await Linking.canOpenURL(url);
                  if (supported) {
                    await Linking.openURL(url);
                  } else {
                    Alert.alert('Error', 'Cannot open Privacy Policy link');
                  }
                }
              } catch (error) {
                Alert.alert('Error', 'Failed to open Privacy Policy');
              }
            }}
          >
            <View style={styles.settingLeft}>
              <Text style={styles.settingIcon}>🔒</Text>
              <View style={styles.settingText}>
                <Text style={styles.settingTitle}>Privacy Policy</Text>
                <Text style={styles.settingSubtitle}>Read our privacy policy</Text>
              </View>
            </View>
            <Text style={styles.settingArrow}>›</Text>
          </TouchableOpacity>

          <TouchableOpacity 
            style={styles.settingItem}
            onPress={async () => {
              const url = 'https://bookapt.io/terms.html';
              try {
                if (Platform.OS === 'web' && typeof window !== 'undefined') {
                  window.open(url, '_blank', 'noopener,noreferrer');
                } else {
                  const supported = await Linking.canOpenURL(url);
                  if (supported) {
                    await Linking.openURL(url);
                  } else {
                    Alert.alert('Error', 'Cannot open Terms of Service link');
                  }
                }
              } catch (error) {
                Alert.alert('Error', 'Failed to open Terms of Service');
              }
            }}
          >
            <View style={styles.settingLeft}>
              <Text style={styles.settingIcon}>📄</Text>
              <View style={styles.settingText}>
                <Text style={styles.settingTitle}>Terms of Service</Text>
                <Text style={styles.settingSubtitle}>Read our terms and conditions</Text>
              </View>
            </View>
            <Text style={styles.settingArrow}>›</Text>
          </TouchableOpacity>

          <TouchableOpacity 
            style={styles.settingItem}
            onPress={async () => {
              const url = 'https://orynsolutions.io/support';
              try {
                if (Platform.OS === 'web' && typeof window !== 'undefined') {
                  window.open(url, '_blank', 'noopener,noreferrer');
                } else {
                  const supported = await Linking.canOpenURL(url);
                  if (supported) {
                    await Linking.openURL(url);
                  } else {
                    Alert.alert('Error', 'Cannot open Help & Support link');
                  }
                }
              } catch (error) {
                Alert.alert('Error', 'Failed to open Help & Support');
              }
            }}
          >
            <View style={styles.settingLeft}>
              <Text style={styles.settingIcon}>❓</Text>
              <View style={styles.settingText}>
                <Text style={styles.settingTitle}>Help & Support</Text>
                <Text style={styles.settingSubtitle}>Get help and support</Text>
              </View>
            </View>
            <Text style={styles.settingArrow}>›</Text>
          </TouchableOpacity>

          <TouchableOpacity 
            style={styles.settingItem}
            onPress={async () => {
              const email = 'info@orynsolutions.io';
              const url = `mailto:${email}`;
              try {
                const supported = await Linking.canOpenURL(url);
                if (supported) {
                  await Linking.openURL(url);
                } else {
                  Alert.alert('Contact Us', `Email: ${email}`);
                }
              } catch (error) {
                Alert.alert('Contact Us', `Email: ${email}`);
              }
            }}
          >
            <View style={styles.settingLeft}>
              <Text style={styles.settingIcon}>📧</Text>
              <View style={styles.settingText}>
                <Text style={styles.settingTitle}>Contact Us</Text>
                <Text style={styles.settingSubtitle}>info@orynsolutions.io</Text>
              </View>
            </View>
            <Text style={styles.settingArrow}>›</Text>
          </TouchableOpacity>
        </View>

        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Account</Text>
          
          <TouchableOpacity style={styles.settingItem} onPress={handleLogout}>
            <View style={styles.settingLeft}>
              <Text style={styles.settingIcon}>→</Text>
              <View style={styles.settingText}>
                <Text style={styles.settingTitle}>Sign out</Text>
                <Text style={styles.settingSubtitle}>Sign out of your account</Text>
              </View>
            </View>
            <Text style={styles.settingArrow}>›</Text>
          </TouchableOpacity>

          <TouchableOpacity
            style={[styles.settingItem, styles.deleteAccountItem]}
            onPress={() => setShowDeleteAccountModal(true)}
            disabled={isDeletingAccount}
          >
            <View style={styles.settingLeft}>
              <Text style={styles.settingIcon}>🗑</Text>
              <View style={styles.settingText}>
                <Text style={styles.deleteAccountTitle}>Delete account</Text>
                <Text style={styles.settingSubtitle}>Permanently delete your account and data</Text>
              </View>
            </View>
            <Text style={styles.settingArrow}>›</Text>
          </TouchableOpacity>
        </View>
      </ScrollView>

      <DeleteAccountConfirmModal
        visible={showDeleteAccountModal}
        onCancel={() => setShowDeleteAccountModal(false)}
        onConfirm={handleDeleteAccountConfirm}
        accountDeletionUrl="https://bookapt.io/privacy.html#account-deletion"
      />
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#f8f9fa',
  },
  header: {
    backgroundColor: '#007bff',
    padding: 20,
    paddingTop: 50,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  headerLeft: {
    flexShrink: 0,
  },
  backButton: {
    backgroundColor: 'rgba(255,255,255,0.2)',
    paddingHorizontal: 10,
    paddingVertical: 6,
    borderRadius: 16,
    alignSelf: 'flex-start',
  },
  backButtonText: {
    color: 'white',
    fontSize: 14,
    fontWeight: '600',
  },
  headerTitle: {
    flex: 1,
    fontSize: 18,
    fontWeight: 'bold',
    color: 'white',
    marginHorizontal: 8,
    flexShrink: 1,
  },
  editButton: {
    backgroundColor: 'rgba(255,255,255,0.2)',
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderRadius: 20,
    flexShrink: 0,
  },
  editButtonText: {
    color: 'white',
    fontSize: 14,
    fontWeight: '600',
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  loadingText: {
    fontSize: 16,
    color: '#666',
  },
  content: {
    flex: 1,
    padding: 20,
  },
  section: {
    marginBottom: 30,
  },
  sectionTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    color: '#333',
    marginBottom: 15,
  },
  inputGroup: {
    marginBottom: 20,
  },
  label: {
    fontSize: 16,
    fontWeight: '600',
    color: '#333',
    marginBottom: 8,
  },
  input: {
    backgroundColor: 'white',
    borderWidth: 1,
    borderColor: '#ddd',
    borderRadius: 8,
    padding: 12,
    fontSize: 16,
    color: '#333',
  },
  multilineInput: {
    height: 80,
    textAlignVertical: 'top',
  },
  valueText: {
    fontSize: 16,
    color: '#666',
    backgroundColor: 'white',
    padding: 12,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#eee',
  },
  buttonContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginTop: 20,
  },
  saveButton: {
    backgroundColor: '#007bff',
    paddingHorizontal: 24,
    paddingVertical: 12,
    borderRadius: 8,
    flex: 1,
    marginRight: 10,
  },
  saveButtonText: {
    color: 'white',
    fontSize: 16,
    fontWeight: '600',
    textAlign: 'center',
  },
  cancelButton: {
    backgroundColor: '#6c757d',
    paddingHorizontal: 24,
    paddingVertical: 12,
    borderRadius: 8,
    flex: 1,
    marginLeft: 10,
  },
  cancelButtonText: {
    color: 'white',
    fontSize: 16,
    fontWeight: '600',
    textAlign: 'center',
  },
  settingItem: {
    backgroundColor: 'white',
    padding: 16,
    borderRadius: 8,
    marginBottom: 10,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  settingLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
  },
  settingIcon: {
    fontSize: 20,
    marginRight: 12,
  },
  settingText: {
    flex: 1,
  },
  settingTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: '#333',
    marginBottom: 2,
  },
  settingSubtitle: {
    fontSize: 14,
    color: '#666',
  },
  settingArrow: {
    fontSize: 20,
    color: '#ccc',
    marginLeft: 10,
  },
  deleteAccountItem: {
    borderColor: '#ffebee',
    borderWidth: 1,
  },
  deleteAccountTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: '#c62828',
    marginBottom: 2,
  },
  toggle: {
    width: 50,
    height: 30,
    borderRadius: 15,
    backgroundColor: '#e0e0e0',
    justifyContent: 'center',
    paddingHorizontal: 2,
  },
  toggleActive: {
    backgroundColor: '#007bff',
  },
  toggleThumb: {
    width: 26,
    height: 26,
    borderRadius: 13,
    backgroundColor: 'white',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.2,
    shadowRadius: 2,
    elevation: 2,
  },
  toggleThumbActive: {
    transform: [{ translateX: 20 }],
  },
  hoursRow: {
    marginBottom: 16,
    paddingBottom: 16,
    borderBottomWidth: 1,
    borderBottomColor: '#F0F0F0',
  },
  hoursDayContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 8,
  },
  hoursDayLabel: {
    fontSize: 16,
    fontWeight: '600',
    color: '#1C1C1E',
    flex: 1,
  },
  toggleSwitch: {
    backgroundColor: '#E1E5E9',
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 16,
    minWidth: 70,
    alignItems: 'center',
  },
  toggleSwitchActive: {
    backgroundColor: '#34C759',
  },
  toggleSwitchText: {
    fontSize: 14,
    fontWeight: '600',
    color: '#FFF',
  },
  hoursTimeContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: 8,
  },
  timeInput: {
    flex: 1,
    backgroundColor: 'white',
    borderWidth: 1,
    borderColor: '#C7C7CC',
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 8,
    fontSize: 16,
    color: '#1C1C1E',
  },
  hoursSeparator: {
    marginHorizontal: 12,
    fontSize: 16,
    color: '#8E8E93',
  },
  durationContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  durationInput: {
    flex: 0,
    width: 80,
    backgroundColor: 'white',
    borderWidth: 1,
    borderColor: '#C7C7CC',
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 10,
    fontSize: 16,
    color: '#1C1C1E',
    textAlign: 'center',
  },
  durationUnit: {
    fontSize: 16,
    color: '#8E8E93',
    fontWeight: '500',
  },
  hoursContainer: {
    backgroundColor: '#F8F9FA',
    borderRadius: 8,
    padding: 12,
    marginTop: 8,
  },
  hoursRowCompact: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingVertical: 8,
    borderBottomWidth: 1,
    borderBottomColor: '#E1E5E9',
  },
  hoursDayRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    minWidth: 100,
  },
  hoursDayLabelCompact: {
    fontSize: 14,
    fontWeight: '600',
    color: '#1C1C1E',
    width: 45,
  },
  toggleSwitchCompact: {
    backgroundColor: '#E1E5E9',
    paddingHorizontal: 10,
    paddingVertical: 4,
    borderRadius: 12,
    minWidth: 60,
    alignItems: 'center',
  },
  hoursTimeContainerCompact: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    flex: 1,
    justifyContent: 'flex-end',
  },
  timeInputCompact: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    minWidth: 90,
    backgroundColor: 'white',
    borderWidth: 1,
    borderColor: '#C7C7CC',
    borderRadius: 6,
    paddingHorizontal: 8,
    paddingVertical: 6,
  },
  timeInputCompactText: {
    fontSize: 14,
    color: '#1C1C1E',
  },
  timeInputCompactChevron: {
    fontSize: 10,
    color: '#8E8E93',
  },
  hoursSeparatorCompact: {
    fontSize: 14,
    color: '#8E8E93',
    marginHorizontal: 4,
  },
  valueTextCompact: {
    fontSize: 14,
    color: '#1C1C1E',
    fontWeight: '500',
  },
  timePickerOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'flex-end',
  },
  timePickerModal: {
    backgroundColor: 'white',
    borderTopLeftRadius: 20,
    borderTopRightRadius: 20,
    maxHeight: '70%',
    paddingBottom: Platform.OS === 'ios' ? 20 : 0,
  },
  timePickerHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 20,
    borderBottomWidth: 1,
    borderBottomColor: '#E1E5E9',
  },
  timePickerTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#1C1C1E',
  },
  timePickerClose: {
    fontSize: 24,
    color: '#8E8E93',
    padding: 5,
  },
  timePickerList: {
    maxHeight: 400,
  },
  timePickerSectionHeader: {
    fontSize: 14,
    fontWeight: '700',
    color: '#8E8E93',
    paddingHorizontal: 16,
    paddingTop: 16,
    paddingBottom: 8,
    backgroundColor: '#F2F2F7',
  },
  timePickerOption: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 16,
    borderBottomWidth: 1,
    borderBottomColor: '#F0F0F0',
  },
  timePickerOptionText: {
    fontSize: 16,
    color: '#1C1C1E',
    flex: 1,
  },
  timePickerCheckmark: {
    fontSize: 18,
    color: '#007AFF',
    fontWeight: 'bold',
    marginLeft: 10,
  },
  queueVisibilitySection: {
    marginTop: 16,
    paddingTop: 16,
    borderTopWidth: StyleSheet.hairlineWidth,
    borderTopColor: '#E1E5E9',
  },
  queueVisibilitySectionTitle: {
    fontSize: 15,
    fontWeight: '700',
    color: '#1C1C1E',
    marginBottom: 6,
  },
  queueVisibilitySectionHint: {
    fontSize: 13,
    color: '#8E8E93',
    marginBottom: 12,
    lineHeight: 18,
  },
  queueVisibilityOption: {
    backgroundColor: '#F8F9FA',
    borderRadius: 10,
    padding: 12,
    marginBottom: 8,
    borderWidth: 1,
    borderColor: '#E1E5E9',
  },
  queueVisibilityOptionSelected: {
    borderColor: '#007bff',
    backgroundColor: '#EEF6FF',
  },
  queueVisibilityOptionTitle: {
    fontSize: 15,
    fontWeight: '600',
    color: '#1C1C1E',
    marginBottom: 4,
  },
  queueVisibilityOptionSubtitle: {
    fontSize: 13,
    color: '#6C6C70',
    lineHeight: 18,
  },
});

export default SettingsScreen;
